/* Workspace shell — Slice D mobile-web (spec: /app/memory/MOBILE_AGENT_PROMPT_ORG_WORKSPACE.md) */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'sonner';
import { Briefcase, User, ChevronLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuthContext } from '../../context/AuthContext';
import { API } from '../../utils/api';
import { NotificationBell } from '../../components/NotificationBell';
import { WTXT } from './workspaceUi';
import { WsHome } from './wsHome';
import { WsOrg } from './wsOrg';
import { WsSub } from './wsSub';

export default function WorkspacePage() {
  // The app has two useAuth() instances (App-level and Gate-level); after an in-app
  // login only the Gate instance is updated. Fall back to localStorage so SPA
  // navigation into /workspace works without a full reload.
  const auth = useAuthContext();
  const token = auth?.token || localStorage.getItem('token');
  const user = useMemo(() => {
    if (auth?.user) return auth.user;
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, [auth?.user]);
  const headers = useMemo(() => ({ Authorization: token ? `Bearer ${token}` : '' }), [token]);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').startsWith('sv') ? 'sv' : 'en';
  const t = WTXT[lang];
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(false);
  const [view, setView] = useState({ name: 'home' });

  const loadMe = useCallback(async () => {
    try {
      setErr(false);
      const res = await axios.get(`${API}/workspace/me`, { headers });
      setMe(res.data);
    } catch (e) { console.error(e); setErr(true); }
  }, [headers]);

  useEffect(() => {
    if (!token || !user) { navigate('/'); return; }
    localStorage.setItem('paymig_ctx', 'workspace');
    loadMe();
  }, [token, user, loadMe, navigate]);

  if (!token || !user) return null;

  const goPersonal = () => {
    localStorage.setItem('paymig_ctx', 'personal');
    navigate('/');
  };
  const goBack = () => setView(view.name === 'sub' ? { name: 'org', org: view.org } : { name: 'home' });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="workspace-page">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-20 h-14 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center gap-2 px-3">
        {view.name !== 'home' ? (
          <button data-testid="ws-back" onClick={goBack}
                  className="p-2 -ml-1 rounded-lg text-slate-500 hover:bg-slate-100" aria-label={t.back}>
            <ChevronLeft size={20} />
          </button>
        ) : (
          <span className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center">
            <Briefcase size={16} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold truncate" data-testid="ws-header-title">
            {view.name === 'home' ? t.ws_title : view.name === 'org' ? view.org.org_name : view.sub?.name || t.ws_title}
          </div>
          {view.name === 'home' && <div className="text-[11px] text-slate-400 truncate">{user.full_name}</div>}
        </div>
        <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden">
          {['en', 'sv'].map((l) => (
            <button key={l} data-testid={`ws-lang-${l}`} onClick={() => i18n.changeLanguage(l)}
              className={`px-2 py-1.5 text-[11px] font-semibold uppercase ${lang === l ? 'bg-teal-600 text-white' : 'text-slate-500'}`}>
              {l}
            </button>
          ))}
        </div>
        <NotificationBell />
        <Button size="sm" variant="outline" data-testid="ws-switch-personal"
                className="h-8 text-[12px] gap-1.5 border-slate-200" onClick={goPersonal}>
          <User size={13} />{t.switch_personal}
        </Button>
      </header>

      <main className="max-w-lg mx-auto p-4 pb-20">
        {err && <p className="text-sm text-rose-600 py-8 text-center">{t.error_load}</p>}
        {!err && !me && <p className="text-sm text-slate-400 py-8 text-center">{t.loading}</p>}
        {me && view.name === 'home' && (
          <WsHome me={me} t={t} lang={lang} headers={headers} reload={loadMe}
                  onOpenOrg={(org) => setView({ name: 'org', org })} />
        )}
        {me && view.name === 'org' && (
          <WsOrg org={view.org} t={t} lang={lang} headers={headers}
                 onOpenSub={(sub) => setView({ name: 'sub', org: view.org, sub })} />
        )}
        {me && view.name === 'sub' && (
          <WsSub org={view.org} sub={view.sub} t={t} lang={lang} headers={headers}
                 onOpenSub={(sub) => setView({ name: 'sub', org: view.org, sub })} />
        )}
      </main>
    </div>
  );
}
